import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import clsx from 'clsx';
import { showToast } from '@utils/toast';
import { importService, contractService, divisionService } from '@services';
import Button from '@components/common/Button';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import type { ScopeRuleUpdatePayload } from '@app-types/import';
import {
  flatRulesToGrouped,
  mergeListRules,
  listRulesToPayload,
} from './scopeRulesHelpers';
import type { ListRule, ScopeRules, ThresholdRule } from './scopeRulesHelpers';
import styles from './ScopeRulesSettings.module.css';

const EMPTY_LOOKUP_HELP = 'Values appear after they are used on contracts or imports.';

const ScopeRulesSettings: React.FC = () => {
  const [rules, setRules] = useState<ScopeRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [tierRules, setTierRules] = useState<ListRule[]>([]);
  const [contractTypeRules, setContractTypeRules] = useState<ListRule[]>([]);
  const [categoryRules, setCategoryRules] = useState<ListRule[]>([]);
  const [divisionRules, setDivisionRules] = useState<ListRule[]>([]);
  const [amountThreshold, setAmountThreshold] = useState<number>(0);
  const [durationThreshold, setDurationThreshold] = useState<number>(60);

  useEffect(() => {
    void fetchRules();
  }, []);

  const fetchRules = async (): Promise<void> => {
    try {
      setLoading(true);
      const [flat, contractTypes, typeRefsRaw, categoryRefsRaw, divisions] = await Promise.all([
        importService.getScopeRules(),
        contractService.getContractTypes().catch(() => [] as string[]),
        importService.getReferenceValues('contract_type').catch(() => [] as string[]),
        importService.getReferenceValues('category').catch(() => [] as string[]),
        divisionService.getActive().catch(() => [] as { id: number; name: string }[]),
      ]);

      const typeRefs = Array.isArray(typeRefsRaw) ? typeRefsRaw : [];
      const categoryRefs = Array.isArray(categoryRefsRaw) ? categoryRefsRaw : [];

      const grouped = flatRulesToGrouped({
        rules: flat.rules.map((rule) => ({
          id: rule.id,
          field: rule.field,
          operator: rule.operator,
          value: rule.value,
          scope: rule.scope,
          created_at: rule.created_at ?? null,
          updated_at: rule.updated_at ?? null,
        })),
        total: flat.total,
      });

      const mergedTypes = mergeListRules(grouped.contract_type, [...contractTypes, ...typeRefs]);
      const mergedCategories = mergeListRules(grouped.category, categoryRefs);
      const mergedDivisions = mergeListRules(grouped.division, divisions.map((division) => division.name));

      setRules(grouped);
      setTierRules(grouped.tier);
      setContractTypeRules(mergedTypes);
      setCategoryRules(mergedCategories);
      setDivisionRules(mergedDivisions);
      setAmountThreshold(grouped.amount?.min_threshold ?? 0);
      setDurationThreshold(grouped.duration?.min_threshold ?? 60);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching scope rules:', error);
      showToast.error('Failed to load scope rules');
    } finally {
      setLoading(false);
    }
  };

  const toggleListRule = (
    setter: React.Dispatch<React.SetStateAction<ListRule[]>>,
    key: string,
  ): void => {
    setter((prev) =>
      prev.map((item) =>
        (item.id !== undefined ? String(item.id) : item.value) === key
          ? { ...item, include_in_scope: !item.include_in_scope }
          : item
      )
    );
    setHasChanges(true);
  };

  const handleAmountChange = (value: string): void => {
    setAmountThreshold(Number(value) || 0);
    setHasChanges(true);
  };

  const handleDurationChange = (value: string): void => {
    setDurationThreshold(Number(value) || 0);
    setHasChanges(true);
  };

  const thresholdPayload = (threshold: ThresholdRule | null, value: number): ScopeRuleUpdatePayload[] => {
    if (!threshold?.id) {
      return [];
    }
    return [{ id: threshold.id, minThreshold: value }];
  };

  const handleSave = async (): Promise<void> => {
    try {
      setSaving(true);

      const rulesToUpdate: ScopeRuleUpdatePayload[] = [
        ...listRulesToPayload('tier', tierRules),
        ...listRulesToPayload('contract_type', contractTypeRules),
        ...listRulesToPayload('category', categoryRules),
        ...listRulesToPayload('division', divisionRules),
        ...thresholdPayload(rules?.amount ?? null, amountThreshold),
        ...thresholdPayload(rules?.duration ?? null, durationThreshold),
      ];

      await importService.updateScopeRules(rulesToUpdate);

      showToast.success('Scope rules saved successfully');
      setHasChanges(false);
      await fetchRules();
    } catch (error) {
      console.error('Error saving scope rules:', error);
      showToast.error('Failed to save scope rules');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async (): Promise<void> => {
    try {
      setRecalculating(true);
      const result = await importService.recalculateScopes();
      setShowRecalcConfirm(false);
      showToast.success(
        `Recalculated ${result.total} contracts (${result.include} included, ${result.exclude} excluded)`
      );
      if (result.errors?.length) {
        showToast.error(`${result.errors.length} contracts failed to recalculate`);
      }
    } catch (error) {
      console.error('Error recalculating scopes:', error);
      showToast.error('Failed to recalculate scopes');
    } finally {
      setRecalculating(false);
    }
  };

  const renderListSection = (
    title: string,
    description: string,
    items: ListRule[],
    onToggle: (key: string) => void,
  ): React.ReactNode => (
    <div className={styles.importSettingsSection}>
      <h3 className={styles.importSettingsSectionTitle}>{title}</h3>
      <p className={styles.importSettingsSectionDescription}>{description}</p>
      {items.length === 0 ? (
        <p className={styles.scopeRulesEmptyHelp}>{EMPTY_LOOKUP_HELP}</p>
      ) : (
        <div className={styles.scopeRulesTierGrid}>
          {items.map((item) => {
            const key = item.id !== undefined ? String(item.id) : item.value;
            return (
              <button
                type="button"
                key={key}
                className={clsx(styles.scopeRulesTierCard, {
                  [styles.scopeRulesTierCardIncluded]: item.include_in_scope,
                  [styles.scopeRulesTierCardExcluded]: !item.include_in_scope
                })}
                onClick={() => onToggle(key)}
              >
                <div className={styles.scopeRulesTierIcon}>
                  {item.include_in_scope ? (
                    <CheckCircle size={24} className={styles.scopeRulesIconSuccess} />
                  ) : (
                    <XCircle size={24} className={styles.scopeRulesIconError} />
                  )}
                </div>
                <div className={styles.scopeRulesTierContent}>
                  <span className={styles.scopeRulesTierName}>{item.value}</span>
                  <span className={clsx(styles.scopeRulesTierStatus, item.include_in_scope && styles.scopeRulesTierStatusIncluded)}>
                    {item.include_in_scope ? 'Included' : 'Excluded'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.importSettingsLoading}>
        <RefreshCw className="spin" size={24} />
        <span>Loading scope rules...</span>
      </div>
    );
  }

  return (
    <div className={styles.importSettings} data-test-id="scope-rules-settings">
      <div className={styles.importSettingsHeader}>
        <div className={styles.importSettingsHeaderContent}>
          <Settings size={24} />
          <div>
            <h2>Scope Calculation Rules</h2>
            <p>Configure rules for automatic contract scope calculation</p>
          </div>
        </div>
        <div className={styles.importSettingsHeaderActions}>
          <Button
            variant="secondary"
            onClick={() => setShowRecalcConfirm(true)}
            disabled={saving || recalculating || hasChanges}
            loading={recalculating}
            loadingText="Recalculating..."
            dataTestId="recalculate-all-button"
          >
            <RefreshCw size={16} />
            Recalculate All
          </Button>
          <Button
            variant="primary"
            onClick={() => { void handleSave(); }}
            disabled={saving || !hasChanges}
            loading={saving}
            loadingText="Saving..."
          >
            <Save size={16} />
            Save Changes
          </Button>
        </div>
      </div>

      <div className={styles.importSettingsInfoBanner}>
        <Info size={16} />
        <span>
          These rules determine whether a contract is included in or excluded from assessments when Assessment Scope is set to Calculate.
          They are applied when creating or updating a contract, during import, and when you run Recalculate All.
        </span>
      </div>

      {renderListSection(
        'Tier Rules',
        'Select which supplier tiers should be included in assessment scope',
        tierRules,
        (key) => toggleListRule(setTierRules, key),
      )}

      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>Threshold Rules</h3>
        <p className={styles.importSettingsSectionDescription}>Set minimum values for contracts to be included in scope</p>

        <div className={styles.scopeRulesThresholdGrid}>
          <div className={styles.scopeRulesThresholdCard}>
            <div className={styles.scopeRulesThresholdHeader}>
              <span className={styles.scopeRulesThresholdLabel}>Minimum Duration</span>
              <AlertTriangle size={16} className={styles.scopeRulesThresholdWarning} />
            </div>
            <div className={styles.scopeRulesThresholdInputGroup}>
              <input
                type="number"
                className={styles.scopeRulesThresholdInput}
                value={durationThreshold}
                onChange={(e) => handleDurationChange(e.target.value)}
                min={0}
                step={1}
              />
              <span className={styles.scopeRulesThresholdUnit}>days</span>
            </div>
            <p className={styles.scopeRulesThresholdHelp}>Contracts shorter than this will be excluded from scope</p>
          </div>

          <div className={styles.scopeRulesThresholdCard}>
            <div className={styles.scopeRulesThresholdHeader}>
              <span className={styles.scopeRulesThresholdLabel}>Minimum Amount</span>
              <AlertTriangle size={16} className={styles.scopeRulesThresholdWarning} />
            </div>
            <div className={styles.scopeRulesThresholdInputGroup}>
              <span className={styles.scopeRulesThresholdCurrency}>$</span>
              <input
                type="number"
                className={styles.scopeRulesThresholdInput}
                value={amountThreshold}
                onChange={(e) => handleAmountChange(e.target.value)}
                min={0}
                step={1000}
              />
            </div>
            <p className={styles.scopeRulesThresholdHelp}>Contracts below this amount will be excluded from scope</p>
          </div>
        </div>
      </div>

      {renderListSection(
        'Contract Types',
        'Select which contract types should be included in assessment scope',
        contractTypeRules,
        (key) => toggleListRule(setContractTypeRules, key),
      )}

      {renderListSection(
        'Categories',
        'Select which categories should be included in assessment scope',
        categoryRules,
        (key) => toggleListRule(setCategoryRules, key),
      )}

      {renderListSection(
        'Divisions',
        'Select which divisions should be included in assessment scope',
        divisionRules,
        (key) => toggleListRule(setDivisionRules, key),
      )}

      {hasChanges && (
        <div className={styles.importSettingsUnsavedBanner}>
          <AlertTriangle size={16} />
          <span>You have unsaved changes. Click &quot;Save Changes&quot; to apply them.</span>
        </div>
      )}

      <ConfirmationModal
        isOpen={showRecalcConfirm}
        onClose={() => setShowRecalcConfirm(false)}
        onConfirm={() => { void handleRecalculate(); }}
        title="Recalculate all contracts?"
        message="Recalculate All re-evaluates every contract against current rules and overwrites existing Include/Exclude."
        confirmLabel="Recalculate All"
        confirmVariant="danger"
        isProcessing={recalculating}
        dataTestId="recalculate-confirmation-modal"
      />
    </div>
  );
};

export default ScopeRulesSettings;
